import React, { useEffect, useMemo, useState } from 'react';
import { Button, DatePicker, Input, Modal, Select, Table, message } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useApolloClient, useMutation, useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import TablePaginationBar from '../../../components/TablePaginationBar';
import SectionCard from './components/SectionCard';
import { ActivityStatusText } from './components/ActivityStatusTag';
import OfflineModal from './components/OfflineModal';
import { confirmOnline } from './components/onlineConfirm';
import { useRuleTemplate } from './hooks/useRuleTemplate';
import {
    GET_DRIVER_ACTIVITY_ADMIN_DETAIL,
    GetDriverActivityAdminDetailResponse,
    GetDriverActivityAdminDetailVariables,
    PAGE_DRIVER_ACTIVITY_ADMIN,
    PageDriverActivityAdminResponse,
    PageDriverActivityAdminVariables,
} from './graphql/queries';
import {
    DELETE_DRIVER_ACTIVITY_DRAFT,
    DeleteDriverActivityDraftResponse,
    DeleteDriverActivityDraftVariables,
    OFFLINE_DRIVER_ACTIVITY,
    OfflineDriverActivityResponse,
    OfflineDriverActivityVariables,
    SUBMIT_DRIVER_ACTIVITY_ONLINE,
    SaveDriverActivityVariables,
    SubmitDriverActivityOnlineResponse,
} from './graphql/mutations';
import { DRIVER_ACTIVITY_STATUS_OPTIONS, DriverActivityAdminFiltersInput, DriverActivityAdminListItem, DriverActivityAdminStatus } from './graphql/types';
import { DEFAULT_PAGE_SIZE, ListFilters, ListQuery, ListReturnState, buildListQuery, parseListQuery } from './utils/listQuery';
import {
    DATE_FORMAT,
    buildSaveInput,
    canDeleteActivity,
    canEditActivity,
    canOfflineActivity,
    canOnlineActivity,
    detailToFormValues,
} from './utils/activityFormUtils';
import { formatActivityLog, submitWithTranslationStaleConfirm } from './utils/submitHelpers';
import { showError } from '../../../utils/exceptionUtils';

/** 日期一律传 yyyy-MM-dd 字符串（美西口径由后端解释），不用 toISOString / 时间戳 */
function toFiltersInput(filters: ListFilters): DriverActivityAdminFiltersInput {
    const [start, end] = filters.dateRange ?? [null, null];
    return {
        name: filters.name?.trim() || null,
        status: filters.status ?? null,
        startDate: start ? start.format(DATE_FORMAT) : null,
        endDate: end ? end.format(DATE_FORMAT) : null,
    };
}

const FilterField: React.FC<{ label: string; width: number; children: React.ReactNode }> = ({ label, width, children }) => (
    <div style={{ width }}>
        <div className="text-[13px] text-[#595959] mb-[6px]">{label}</div>
        {children}
    </div>
);

/**
 * 司机活动列表（原型页面 1）：筛选（名称模糊 / 状态 / 活动时间交集）+ 列表 + 操作列。
 * 默认按创建时间倒序由后端保证。
 * 筛选条件和页码放在地址栏参数里：进详情 / 编辑再返回、刷新页面都不丢。
 */
const DriverActivityListPage: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const client = useApolloClient();
    const { template, loading: templateLoading } = useRuleTemplate();
    const [searchParams, setSearchParams] = useSearchParams();
    const { filters, page, pageSize } = useMemo(() => parseListQuery(searchParams), [searchParams]);
    const [tempFilters, setTempFilters] = useState<ListFilters>(filters);
    // 地址栏里的筛选变了（点左侧菜单回到干净列表、浏览器前进后退）时，输入框跟着回显；只翻页不动输入框
    const filterKey = buildListQuery({ filters, page: 1, pageSize: DEFAULT_PAGE_SIZE }).toString();
    useEffect(() => {
        setTempFilters(filters);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filterKey]);
    const [offlineTarget, setOfflineTarget] = useState<DriverActivityAdminListItem | null>(null);
    const [actionLoading, setActionLoading] = useState(false);

    const variables = useMemo<PageDriverActivityAdminVariables>(
        () => ({ filters: toFiltersInput(filters), page, size: pageSize }),
        [filters, page, pageSize]
    );
    const { data, loading, refetch } = useQuery<PageDriverActivityAdminResponse, PageDriverActivityAdminVariables>(PAGE_DRIVER_ACTIVITY_ADMIN, {
        variables,
        fetchPolicy: 'network-only',
        notifyOnNetworkStatusChange: true,
    });
    const items = data?.pageDriverActivityAdmin?.items ?? [];
    const total = data?.pageDriverActivityAdmin?.total ?? 0;

    const [submitOnline] = useMutation<SubmitDriverActivityOnlineResponse, SaveDriverActivityVariables>(SUBMIT_DRIVER_ACTIVITY_ONLINE);
    const [offlineActivity] = useMutation<OfflineDriverActivityResponse, OfflineDriverActivityVariables>(OFFLINE_DRIVER_ACTIVITY);
    const [deleteDraft] = useMutation<DeleteDriverActivityDraftResponse, DeleteDriverActivityDraftVariables>(DELETE_DRIVER_ACTIVITY_DRAFT);

    /** replace 而不是 push：查询 / 翻页不堆浏览器历史，后退键直接离开列表页 */
    const updateQuery = (next: ListQuery) => setSearchParams(buildListQuery(next), { replace: true });

    /** 跳详情 / 编辑 / 新建都带上当前筛选，那边返回列表时原样带回 */
    const goTo = (path: string) => {
        const state: ListReturnState = { listSearch: location.search };
        navigate(path, { state });
    };

    const handleSearch = () => {
        updateQuery({ filters: tempFilters, page: 1, pageSize });
    };

    const handleReset = () => {
        setTempFilters({});
        updateQuery({ filters: {}, page: 1, pageSize });
    };

    const handlePageChange = (nextPage: number, nextSize: number) => {
        if (nextSize !== pageSize) {
            updateQuery({ filters, page: 1, pageSize: nextSize });
        } else {
            updateQuery({ filters, page: nextPage, pageSize });
        }
    };

    /**
     * 列表「上线」：契约只有 submitDriverActivityOnline(input: SaveDriverActivityInput!)，没有按 id 上线的接口，
     * 所以先拉详情转成完整 input 再提交；确认弹窗同时展示开始时间和报名截止。
     */
    const handleOnline = async (record: DriverActivityAdminListItem) => {
        // 模板没到就用兜底模板重组文案，段定义一旦和后端不一致会把多出的段写空，等一下再点
        if (templateLoading) {
            message.warning(t('driverOps.activity.message.templateLoading'));
            return;
        }
        setActionLoading(true);
        try {
            const { data: detailData } = await client.query<GetDriverActivityAdminDetailResponse, GetDriverActivityAdminDetailVariables>({
                query: GET_DRIVER_ACTIVITY_ADMIN_DETAIL,
                variables: { id: record.id },
                fetchPolicy: 'network-only',
            });
            const detail = detailData?.getDriverActivityAdminDetail;
            if (!detail) return;
            const values = detailToFormValues(detail, template);
            confirmOnline({
                t,
                name: detail.name,
                startDate: detail.startDate,
                signUpDeadline: detail.needSignUp ? detail.signUpDeadline : null,
                onOk: async () => {
                    const ok = await submitWithTranslationStaleConfirm(t, (ignore) =>
                        submitOnline({ variables: { input: buildSaveInput(values, template, detail.id, ignore) } })
                    );
                    if (ok) {
                        message.success(t('driverOps.activity.message.onlineSuccess'), 3);
                        void refetch();
                    }
                },
            });
        } catch (e) {
            showError(e);
        } finally {
            setActionLoading(false);
        }
    };

    const handleOffline = async (reason: string) => {
        if (!offlineTarget) return;
        setActionLoading(true);
        try {
            await offlineActivity({ variables: { id: offlineTarget.id, reason } });
            message.success(t('driverOps.activity.message.offlineSuccess'), 3);
            setOfflineTarget(null);
            void refetch();
        } catch (e) {
            showError(e);
        } finally {
            setActionLoading(false);
        }
    };

    const handleDelete = (record: DriverActivityAdminListItem) => {
        Modal.confirm({
            width: 460,
            title: t('driverOps.activity.deleteConfirm.title'),
            content: t('driverOps.activity.deleteConfirm.content', { name: record.name }),
            okText: t('driverOps.activity.deleteConfirm.ok'),
            okButtonProps: { danger: true },
            cancelText: t('common.cancel'),
            onOk: async () => {
                try {
                    await deleteDraft({ variables: { id: record.id } });
                    message.success(t('driverOps.activity.message.deleteSuccess'), 3);
                    void refetch();
                } catch (e) {
                    showError(e);
                }
            },
        });
    };

    const link = (label: string, onClick: () => void, danger = false) => (
        <span
            key={label}
            className={`cursor-pointer mr-[14px] whitespace-nowrap ${danger ? 'text-[#F0453A]' : 'text-[#722ed1]'}`}
            onClick={onClick}
        >
            {label}
        </span>
    );

    const columns: ColumnsType<DriverActivityAdminListItem> = [
        { title: t('driverOps.activity.table.id'), dataIndex: 'id', width: 90 },
        {
            title: t('driverOps.activity.table.name'),
            dataIndex: 'name',
            // 名称最多 20 字，给固定宽；剩余空间让给日志列（宽屏下名称列不再撑满半屏）
            width: 260,
            ellipsis: true,
            render: (name: string, record) => (
                <span
                    className="text-[#722ed1] underline underline-offset-[3px] cursor-pointer"
                    title={name}
                    onClick={() => goTo(`/driver-ops/activity/${record.id}`)}
                >
                    {name}
                </span>
            ),
        },
        {
            title: t('driverOps.activity.table.type'),
            dataIndex: 'type',
            width: 100,
            render: (type: string) => t(`driverOps.activity.type.${type}`, { defaultValue: type }),
        },
        {
            title: t('driverOps.activity.table.time'),
            width: 250,
            render: (_, record) => `${record.startDate ?? '--'} ~ ${record.endDate ?? '--'}`,
        },
        {
            title: t('driverOps.activity.table.status'),
            dataIndex: 'status',
            width: 100,
            render: (status: DriverActivityAdminStatus) => <ActivityStatusText status={status} />,
        },
        {
            title: t('driverOps.activity.table.log'),
            render: (_, record) => (
                <span className="text-[13px] text-[#8C8C8C]">{record.latestLog ? formatActivityLog(record.latestLog, t) : '--'}</span>
            ),
        },
        {
            title: t('common.action'),
            width: 150,
            render: (_, record) => {
                const actions: React.ReactNode[] = [];
                if (canEditActivity(record.status)) {
                    actions.push(link(t('driverOps.activity.action.edit'), () => goTo(`/driver-ops/activity/${record.id}/edit`)));
                }
                if (canOnlineActivity(record.status)) {
                    actions.push(link(t('driverOps.activity.action.online'), () => void handleOnline(record)));
                }
                if (canOfflineActivity(record.status)) {
                    actions.push(link(t('driverOps.activity.action.offline'), () => setOfflineTarget(record), true));
                }
                if (canDeleteActivity(record.status)) {
                    actions.push(link(t('driverOps.activity.action.delete'), () => handleDelete(record), true));
                }
                return actions.length ? actions : <span className="text-[#BFBFBF]">—</span>;
            },
        },
    ];

    const statusOptions = DRIVER_ACTIVITY_STATUS_OPTIONS.map((status) => ({
        value: status,
        label: t(`driverOps.activity.status.${status}`),
    }));

    return (
        <div>
            <h1 className="text-2xl font-bold mb-1">{t('driverOps.activity.title')}</h1>

            <div className="bg-white border border-[#EFEFEF] rounded-[10px] px-[22px] py-[18px] mb-[18px]">
                <div className="flex gap-[14px] items-end flex-wrap">
                    <FilterField label={t('driverOps.activity.filter.name')} width={240}>
                        <Input
                            allowClear
                            value={tempFilters.name}
                            placeholder={t('driverOps.activity.filter.namePlaceholder') as string}
                            onChange={(e) => setTempFilters({ ...tempFilters, name: e.target.value || undefined })}
                            onPressEnter={handleSearch}
                        />
                    </FilterField>
                    <FilterField label={t('driverOps.activity.filter.status')} width={200}>
                        <Select
                            className="w-full"
                            allowClear
                            options={statusOptions}
                            value={tempFilters.status}
                            placeholder={t('common.all') as string}
                            onChange={(status?: DriverActivityAdminStatus) => setTempFilters({ ...tempFilters, status })}
                        />
                    </FilterField>
                    <FilterField label={t('driverOps.activity.filter.time')} width={300}>
                        <DatePicker.RangePicker
                            className="w-full"
                            allowEmpty={[true, true]}
                            format={DATE_FORMAT}
                            value={tempFilters.dateRange ?? null}
                            placeholder={[t('driverOps.activity.filter.startDate'), t('driverOps.activity.filter.endDate')]}
                            onChange={(range) => setTempFilters({ ...tempFilters, dateRange: range as ListFilters['dateRange'] })}
                        />
                    </FilterField>
                    <Button type="primary" onClick={handleSearch}>
                        {t('common.search')}
                    </Button>
                    <Button onClick={handleReset}>{t('common.reset')}</Button>
                </div>
            </div>

            <SectionCard
                title={t('driverOps.activity.listTitle')}
                extra={
                    <div className="flex items-center gap-2">
                        {/* 上线 / 下线 / 删除后会自动重拉，这个按钮给运营手动刷新用（列表 loading 一闪而过不明显） */}
                        <Button icon={<ReloadOutlined />} loading={loading} onClick={() => void refetch()}>
                            {t('common.refresh')}
                        </Button>
                        <Button type="primary" onClick={() => goTo('/driver-ops/activity/new')}>
                            {t('driverOps.activity.action.create')}
                        </Button>
                    </div>
                }
            >
                <Table<DriverActivityAdminListItem>
                    rowKey="id"
                    columns={columns}
                    dataSource={items}
                    loading={loading || actionLoading}
                    pagination={false}
                    scroll={{ x: 'max-content' }}
                />
                <TablePaginationBar total={total} current={page} pageSize={pageSize} onChange={handlePageChange} className="pt-[14px]" />
            </SectionCard>

            <OfflineModal open={offlineTarget !== null} loading={actionLoading} onCancel={() => setOfflineTarget(null)} onOk={handleOffline} />
        </div>
    );
};

export default DriverActivityListPage;
